class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    // Auto-submit only for sort_by radio buttons, not for filters
    this.debouncedOnSubmit = debounce((event) => {
      if (event.target && event.target.name === 'sort_by') {
        this.onSubmitHandler(event);
      }
    }, 500);

    const formEl = this.querySelector('form');
    if (formEl) {
      // Listen to both 'input' and 'change' events for sort_by (radio buttons fire 'change')
      formEl.addEventListener('input', this.debouncedOnSubmit.bind(this));
      formEl.addEventListener('change', this.debouncedOnSubmit.bind(this));
    }

    const closeButton = document.querySelector('.facet-drawer__close-button');
    if (closeButton) {
      closeButton.addEventListener('click', (event) => this.closeMenuDrawer(event, closeButton));
    }
  }

  connectedCallback() {
    // Ensure single-open behavior is applied on initial load
    FacetFiltersForm.bindSingleOpenBehavior();
    // Bind mobile apply button on initial load
    FacetFiltersForm.bindMobileApplyButton();
  }

  closeMenuDrawer(event) {
    event.preventDefault();
    const transition_body = document.querySelector('.transition-body');
    transition_body.click();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const facetDrawer = document.getElementById('FacetDrawer');
    const countContainer = document.getElementById('ProductCount');
    const mobileCountEls = document.querySelectorAll('.ProductCountMobile');
    const desktopCountEls = document.querySelectorAll('.ProductCountDesktop');

    // show loading
    // close any open filter dropdowns so they don't sit above the overlay
    document.querySelectorAll('details.js-filter[open]').forEach((el) => el.removeAttribute('open'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) countContainer.classList.add('loading');
    mobileCountEls.forEach((el) => el.classList.add('loading'));
    desktopCountEls.forEach((el) => el.classList.add('loading'));
    if (facetDrawer) facetDrawer.classList.add('loading');

    // fetch/render each section (unchanged)…
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const existsInCache = (fd) => fd.url === url;
      if (FacetFiltersForm.filterData.some(existsInCache)) {
        FacetFiltersForm.renderSectionFromCache(existsInCache, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    document.dispatchEvent(new CustomEvent('collection:reloaded'));
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          {
            html,
            url,
          },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;

    const layoutSwitcher = document.querySelector('#FacetSortFiltersForm layout-switcher');
    if (layoutSwitcher) layoutSwitcher.onButtonClick(layoutSwitcher.querySelector('.list-view__item--active'));
    // Ensure any lingering loading states are cleared after DOM replacement
    document.querySelectorAll('#ProductGridContainer .collection.loading').forEach((el) => el.classList.remove('loading'));

    // Re-run availability filter now that the new product cards are in the DOM.
    // The collection:reloaded event fires before the async fetch completes, so the
    // listener runs on the old DOM and misses products inserted by this render.
    filterProductsByVariantAvailability();
  }

  static renderProductCount(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // the “master” count still comes from your unique #ProductCount
    const count = doc.getElementById('ProductCount').innerHTML;

    // update main count
    const mainEl = document.getElementById('ProductCount');
    if (mainEl) {
      mainEl.innerHTML = count;
      mainEl.classList.remove('loading');
    }

    // update *all* mobile counts
    document.querySelectorAll('.ProductCountMobile').forEach((el) => {
      el.innerHTML = count;
      el.classList.remove('loading');
    });

    // update *all* desktop counts
    document.querySelectorAll('.ProductCountDesktop').forEach((el) => {
      el.innerHTML = count;
      el.classList.remove('loading');
    });
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll('#FacetSortFiltersForm .js-filter, #FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');

    // Before replacing innerHTML, remove any panels that are currently portaled to body.
    // If a panel is portaled when innerHTML is replaced, the placeholder comment is destroyed
    // and the panel can never be returned — causing it to accumulate as an orphan at body.
    document.querySelectorAll('body > .facets__dropdown-panel').forEach((el) => el.remove());

    // Update ALL filters with the new HTML from server (including the one that was just modified)
    facetDetailsElements.forEach((element) => {
      const target = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      if (target) {
        target.innerHTML = element.innerHTML;
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    const facetDrawer = document.getElementById('FacetDrawer');
    if (facetDrawer) {
      facetDrawer.classList.remove('loading');
    }

    // Ensure only one filter dropdown is open at a time (desktop and mobile)
    FacetFiltersForm.bindSingleOpenBehavior();

    // Bind mobile apply button
    FacetFiltersForm.bindMobileApplyButton();
  }

  static bindSingleOpenBehavior() {
    const containers = [document.getElementById('FacetSortFiltersForm'), document.getElementById('FacetFiltersForm'), document.getElementById('FacetFiltersFormMobile')].filter(Boolean);

    containers.forEach((container) => {
      const detailsList = Array.from(container.querySelectorAll('details.js-filter'));
      detailsList.forEach((detailsEl) => {
        // Remove any previous listener by cloning (safe reset for re-rendered DOM)
        const cloned = detailsEl.cloneNode(true);
        detailsEl.parentNode.replaceChild(cloned, detailsEl);
      });
    });

    containers.forEach((container) => {
      const detailsList = Array.from(container.querySelectorAll('details.js-filter'));
      detailsList.forEach((detailsEl) => {
        detailsEl.addEventListener('toggle', () => {
          if (!detailsEl.open) return;
          detailsList.forEach((other) => {
            if (other !== detailsEl && other.open) {
              other.removeAttribute('open');
            }
          });
          FacetFiltersForm.positionFloatingPanel(detailsEl);
        });

        // Special handling for sort dropdown - add direct auto-submit listener
        const isSortDropdown = detailsEl.dataset.index === 'sort';
        if (isSortDropdown) {
          // Ensure mobile close button works for sort dropdown
          const closeBtn = detailsEl.querySelector('.facets__mobile-close');
          if (closeBtn) {
            closeBtn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              detailsEl.removeAttribute('open');
            });
          }
          const sortInputs = detailsEl.querySelectorAll('input[name="sort_by"]');
          sortInputs.forEach((input) => {
            input.addEventListener('change', (e) => {
              const facetForm = detailsEl.closest('facet-filters-form');
              if (facetForm) {
                const formEl = facetForm.querySelector('form');
                if (formEl) {
                  // Close the dropdown immediately
                  detailsEl.removeAttribute('open');
                  // Create a synthetic event with the form as target
                  const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                  Object.defineProperty(submitEvent, 'target', { value: formEl, enumerable: true });
                  // Submit the form
                  facetForm.onSubmitHandler(submitEvent);

                  // Mobile-only: fully close sorting modal/drawer after selection
                  if (window.matchMedia('(max-width: 749px)').matches) {
                    // short delay to let request start
                    setTimeout(() => {
                      const drawerCloseBtn = document.querySelector('.facet-drawer__close-button, facet-drawer button[data-close], menu-drawer button[data-close]');
                      if (drawerCloseBtn) {
                        try {
                          drawerCloseBtn.click();
                        } catch (_) {}
                      } else {
                        FacetFiltersForm.forceCloseAllDrawers('sort-mobile');
                      }
                      const mobileFormEl = document.getElementById('FacetFiltersFormMobile');
                      if (mobileFormEl) {
                        mobileFormEl.classList.add('mobile-facets--closed');
                        mobileFormEl.style.display = 'none';
                        mobileFormEl.style.visibility = 'hidden';
                        mobileFormEl.style.pointerEvents = 'none';
                      }
                      document.body.classList.remove('facet-drawer--open', 'facet-drawer--opening', 'facet-drawer--closing');
                      document.body.classList.remove('menu-drawer--open', 'menu-drawer--opening', 'menu-drawer--closing');
                    }, 50);
                    // Final cleanup once products reloaded
                    const onceHandler = () => {
                      FacetFiltersForm.forceCloseAllDrawers('sort-mobile-reloaded');
                      document.removeEventListener('collection:reloaded', onceHandler);
                    };
                    document.addEventListener('collection:reloaded', onceHandler);
                  }
                }
              }
            });
          });
          return; // Skip apply button logic for sort dropdown
        }

        // Add mobile close button listener
        const closeBtn = detailsEl.querySelector('.facets__mobile-close');
        if (closeBtn) {
          closeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            detailsEl.removeAttribute('open');
          });
        }

        // Add apply button listener
        const applyBtn = detailsEl.querySelector('.facets__apply-button');
        if (applyBtn) {
          // Update button count when filters change
          const updateButtonCount = () => {
            const form = detailsEl.closest('facet-filters-form');
            if (!form) return;

            const formEl = form.querySelector('form');
            if (!formEl) return;

            // Create FormData from current form state
            const formData = new FormData(formEl);
            const searchParams = new URLSearchParams(formData).toString();

            // Fetch the updated count without reloading the page
            const sections = FacetFiltersForm.getSections();
            const section = sections[0];
            const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;

            fetch(url)
              .then((response) => response.text())
              .then((html) => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const countEl = doc.getElementById('ProductCount');
                if (countEl) {
                  const countText = countEl.textContent.trim();
                  // Extract number from text like "150 af 500 produkter" or "150 produkter"
                  const match = countText.match(/(\d+)/);
                  if (match) {
                    const count = match[1];
                    // Update button text
                    const buttonText = applyBtn.textContent.replace(/\(\d+\)/, `(${count})`);
                    applyBtn.textContent = buttonText;
                    applyBtn.dataset.count = count;
                  }
                }
              })
              .catch((e) => console.error('Error updating count:', e));
          };

          // Listen for input changes in the filter
          const inputs = detailsEl.querySelectorAll('input[type="checkbox"], input[type="radio"]');
          inputs.forEach((input) => {
            input.addEventListener('change', updateButtonCount);
          });

          // Also listen for price range slider changes
          const priceRange = detailsEl.querySelector('price-range');
          if (priceRange) {
            // Listen for mouseup on the slider handles (when user releases the slider)
            const handles = priceRange.querySelectorAll('.price-range__thumbs');
            handles.forEach((handle) => {
              handle.addEventListener('mouseup', () => {
                // Small delay to ensure inputs are updated
                setTimeout(updateButtonCount, 100);
              });
            });

            // Listen for input changes on the text fields
            const rangeInputs = priceRange.querySelectorAll('input[type="number"]');
            rangeInputs.forEach((input) => {
              input.addEventListener('change', () => {
                setTimeout(updateButtonCount, 100);
              });
              input.addEventListener('blur', () => {
                setTimeout(updateButtonCount, 100);
              });
            });
          }

          applyBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            // Force update price range inputs before submitting
            const priceRange = detailsEl.querySelector('price-range');
            if (priceRange) {
              const inputs = priceRange.querySelectorAll('input[type="number"]');
              inputs.forEach((input) => {
                // Trigger change to ensure value is captured
                input.dispatchEvent(new Event('change', { bubbles: true }));
              });
            }

            // Get the closest facet-filters-form (which contains the actual form)
            const facetForm = detailsEl.closest('facet-filters-form');
            if (facetForm) {
              const formEl = facetForm.querySelector('form');
              if (formEl) {
                // Create a synthetic event that targets the form
                const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                Object.defineProperty(submitEvent, 'target', { value: formEl, enumerable: true });

                // Call the handler directly
                facetForm.onSubmitHandler(submitEvent);
              }
            }

            // Close the dropdown & drawer after a short delay to allow submission to start
            setTimeout(() => {
              // Close just this dropdown
              detailsEl.removeAttribute('open');

              // Attempt native drawer close buttons first
              const drawerCloseBtn = document.querySelector('.facet-drawer__close-button, facet-drawer button[data-close], menu-drawer button[data-close]');
              if (drawerCloseBtn) {
                try {
                  drawerCloseBtn.click();
                } catch (_) {}
              } else {
                // Fallback: force close all drawers
                FacetFiltersForm.forceCloseAllDrawers('apply-btn');
              }

              // Hide mobile form explicitly to remove ghost visual layer
              const mobileFormEl = document.getElementById('FacetFiltersFormMobile');
              if (mobileFormEl) {
                mobileFormEl.classList.add('mobile-facets--closed');
                mobileFormEl.style.display = 'none';
                mobileFormEl.style.visibility = 'hidden';
                mobileFormEl.style.pointerEvents = 'none';
              }

              // Remove body classes that might keep overlay styling
              document.body.classList.remove('facet-drawer--open', 'facet-drawer--opening', 'facet-drawer--closing');
              document.body.classList.remove('menu-drawer--open', 'menu-drawer--opening', 'menu-drawer--closing');
            }, 100);
          });
        }
      });
    });
  }

  static bindMobileApplyButton() {
    const mobileFormEl = document.getElementById('FacetFiltersFormMobile');
    if (!mobileFormEl) return;
    const applyBtn = mobileFormEl.querySelector('.js-mobile-apply-filters');
    if (!applyBtn) return;
    // Rebind safely (remove previous listener if exists)
    if (applyBtn.__handler) {
      applyBtn.removeEventListener('click', applyBtn.__handler);
    }

    const forceCloseDrawer = () => {
      // Find any drawer variants that might contain filters
      const possibleDrawers = Array.from(document.querySelectorAll('facet-drawer, menu-drawer, header-drawer'));
      possibleDrawers.forEach((drawer) => {
        // Close main details toggle if present
        const mainDetails = drawer.querySelector('details[open]');
        if (mainDetails) {
          // Use native API if available
          if (drawer.closeMenuDrawer) {
            try {
              drawer.closeMenuDrawer(new Event('close'), mainDetails.querySelector('summary'));
            } catch (_) {
              mainDetails.removeAttribute('open');
            }
          } else {
            mainDetails.removeAttribute('open');
          }
        }
        // Strip body classes related to drawer states (defensive)
        document.body.classList.remove('facet-drawer--open', 'facet-drawer--opening', 'facet-drawer--closing', 'menu-drawer--open', 'menu-drawer--opening', 'menu-drawer--closing');
      });
    };

    applyBtn.__handler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const formData = new FormData(mobileFormEl);
      const params = new URLSearchParams();

      for (const [key, value] of formData.entries()) {
        if (value === '') continue;
        params.append(key, value);
      }

      // Same preservation logic as createSearchParams: if the mobile form doesn't
      // own the availability input, carry the current URL value forward.
      if (!mobileFormEl.querySelector('[name="filter.v.availability"]')) {
        const currentAvail = new URLSearchParams(window.location.search).get('filter.v.availability');
        if (currentAvail) params.append('filter.v.availability', currentAvail);
      }

      // Auto-apply availability whenever a variant option filter (e.g. size) is active.
      if ([...params.keys()].some((k) => k.startsWith('filter.v.option.')) && !params.has('filter.v.availability')) {
        params.append('filter.v.availability', '1');
      }

      // Render first (fetch starts)
      FacetFiltersForm.renderPage(params.toString(), e);
      // After tiny delay, invoke native close button (if drawer pattern exists)
      setTimeout(() => {
        const nativeClose = document.querySelector('facet-drawer button[data-close], menu-drawer button[data-close]');
        if (nativeClose) nativeClose.click();
        else FacetFiltersForm.forceCloseAllDrawers('fallback-pre');
      }, 10);
      // Final cleanup once collection reloaded (event fired at end of renderPage)
      const onceHandler = () => {
        FacetFiltersForm.forceCloseAllDrawers('reloaded');
        document.removeEventListener('collection:reloaded', onceHandler);
      };
      document.addEventListener('collection:reloaded', onceHandler);
    };
    applyBtn.addEventListener('click', applyBtn.__handler);
    // Also bind the explicit mobile close button to unified close logic (if not already bound)
    const mobileExplicitClose = mobileFormEl.querySelector('.mobile-facets__close');
    if (mobileExplicitClose && !mobileExplicitClose.__boundMobileClose) {
      mobileExplicitClose.__boundMobileClose = true;
      mobileExplicitClose.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        FacetFiltersForm.closeMobileFilters();
      });
    }
  }

  static closeMobileFilters() {
    const mobileFormEl = document.getElementById('FacetFiltersFormMobile');
    if (!mobileFormEl) return;
    // Close any open mobile filter detail panels
    mobileFormEl.querySelectorAll('details.js-filter[open]').forEach((d) => {
      d.removeAttribute('open');
      d.classList.remove('mobile-filter-active', 'mobile-filter-closing');
    });
    // Remove floating panels that might still be portaled
    document.querySelectorAll('.facets__dropdown-panel--floating').forEach((panel) => {
      if (panel.__portaled) {
        try {
          const { originalParent, placeholder } = panel.__portaled;
          if (originalParent && placeholder && placeholder.parentNode) {
            originalParent.insertBefore(panel, placeholder);
            placeholder.remove();
          }
        } catch (_) {}
        panel.__portaled = null;
      }
      panel.classList.remove('is-revealed');
      panel.style.visibility = 'hidden';
      panel.style.opacity = '0';
    });
    // Remove any body classes related to drawer state (defensive)
    document.body.classList.remove('facet-drawer--open', 'facet-drawer--opening');
  }

  static forceCloseAllDrawers(contextTag = '') {
    try {
      const drawers = Array.from(document.querySelectorAll('facet-drawer, menu-drawer, header-drawer'));
      const hadBodyClass = document.body.classList.contains('facet-drawer--open') || document.body.classList.contains('menu-drawer--open');
      drawers.forEach((drawer) => {
        // Dispatch force-close event (handled by MenuDrawer.bindEvents)
        drawer.dispatchEvent(new CustomEvent('drawer:force-close', { bubbles: true }));
        // Remove any open attributes inside
        drawer.querySelectorAll('details[open]').forEach((d) => d.removeAttribute('open'));
      });
      // Mobile standalone form (not inside drawer) clean-up
      const mobileFormEl = document.getElementById('FacetFiltersFormMobile');
      if (mobileFormEl) {
        mobileFormEl.querySelectorAll('details[open]').forEach((d) => d.removeAttribute('open'));
        // Hide mobile form if drawer fully closed to avoid visual overlay persisting with click-through
        if (!document.body.classList.contains('facet-drawer--open') && !document.body.classList.contains('menu-drawer--open')) {
          mobileFormEl.classList.add('mobile-facets--closed');
          mobileFormEl.style.visibility = 'hidden';
          mobileFormEl.style.pointerEvents = 'none';
        }
      }
      // Body class cleanup
      document.body.classList.remove('facet-drawer--open', 'facet-drawer--opening', 'facet-drawer--closing', 'menu-drawer--open', 'menu-drawer--opening', 'menu-drawer--closing');
      // Floating panel cleanup
      FacetFiltersForm.closeMobileFilters();
    } catch (err) {}
  }

  static positionFloatingPanel(detailsEl) {
    const summaryEl = detailsEl.querySelector('summary');
    const panelEl = detailsEl.querySelector('.facets__dropdown-panel');
    if (!summaryEl || !panelEl) return;

    // Capture trigger rect BEFORE portaling to avoid initial 0,0 flash
    const triggerRect = summaryEl.getBoundingClientRect();
    const originalParent = panelEl.parentNode;
    const placeholder = document.createComment('facets-dropdown-placeholder');
    const sourceFacetForm = originalParent.closest && originalParent.closest('facet-filters-form');
    const sourceFormEl = sourceFacetForm ? sourceFacetForm.querySelector('form') : null;
    if (!panelEl.__portaled) {
      originalParent.insertBefore(placeholder, panelEl);
      document.body.appendChild(panelEl);
      panelEl.__portaled = { originalParent, placeholder };

      // When the panel is fixed-positioned but its scroll container doesn't overflow,
      // the browser's focus-scroll propagates to window and scrolls the page.
      // Save scroll Y on mousedown, restore it before the next paint in focusin.
      let _savedScrollY = null;
      const onPreventScrollMouseDown = () => {
        _savedScrollY = window.pageYOffset;
      };
      const onPreventScrollFocusIn = () => {
        if (_savedScrollY !== null) {
          const y = _savedScrollY;
          _savedScrollY = null;
          requestAnimationFrame(() => window.scrollTo(window.pageXOffset, y));
        }
      };
      panelEl.__preventScroll = { onPreventScrollMouseDown, onPreventScrollFocusIn };
      panelEl.addEventListener('mousedown', onPreventScrollMouseDown, true);
      panelEl.addEventListener('focusin', onPreventScrollFocusIn, true);
    }

    const applyFloatingStyles = () => {
      if (panelEl.__floatingApplied) return; // only first time
      panelEl.__floatingApplied = true;
      panelEl.classList.add('facets__dropdown-panel--floating');
      panelEl.style.position = 'fixed';
      panelEl.style.left = '0px';
      panelEl.style.top = '0px';
      panelEl.style.visibility = 'hidden';
      panelEl.style.display = 'block';
      panelEl.__openedAt = performance.now();

      if (sourceFormEl && sourceFormEl.id) {
        panelEl.querySelectorAll('input, select, textarea, button').forEach((el) => {
          try {
            if (!el.getAttribute('form')) el.setAttribute('form', sourceFormEl.id);
          } catch (_) {}
        });
      }
    };

    const clearFloatingStyles = () => {
      panelEl.classList.remove('facets__dropdown-panel--floating');
      panelEl.removeEventListener('change', onPanelInputProxy, true);
      panelEl.removeEventListener('input', onPanelInputProxy, true);
      if (panelEl.__preventScroll) {
        panelEl.removeEventListener('mousedown', panelEl.__preventScroll.onPreventScrollMouseDown, true);
        panelEl.removeEventListener('focusin', panelEl.__preventScroll.onPreventScrollFocusIn, true);
        panelEl.__preventScroll = null;
      }
      panelEl.style.position = '';
      panelEl.style.left = '';
      panelEl.style.right = '';
      panelEl.style.top = '';
      panelEl.style.visibility = '';
      panelEl.style.display = '';
      if (panelEl.__portaled) {
        const { originalParent, placeholder } = panelEl.__portaled;
        if (originalParent && placeholder && placeholder.parentNode) {
          originalParent.insertBefore(panelEl, placeholder);
          placeholder.remove();
        } else {
          // Placeholder was removed by an AJAX re-render while this panel was portaled
          // (race condition: user opened a filter while a previous fetch was in flight).
          // The filter HTML has already been replaced with fresh server content, so this
          // panel is orphaned. Remove it — leaving it at body causes duplicate input ids
          // which makes the browser scroll to the wrong (unfixed) element on focus.
          panelEl.remove();
        }
        panelEl.__portaled = null;
      }
    };

    const REVEAL_DELAY_MS = 100; // standard delay for filter panels
    const REVEAL_DELAY_MS_SORT = 0; // instant reveal for sort dropdown to keep UX snappy
    const placePanel = (isReposition = false) => {
      // Use latest rect only for reposition; initial uses pre-portal rect to prevent jump
      const rect = isReposition ? summaryEl.getBoundingClientRect() : triggerRect;
      applyFloatingStyles();
      const panelHeight = panelEl.offsetHeight;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const prefersUp = spaceBelow < 250 && spaceAbove > spaceBelow;
      const gap = 8;
      let top = prefersUp ? rect.top - panelHeight - gap : rect.bottom + gap;
      if (top < 8) top = 8;
      if (top + panelHeight > window.innerHeight - 8) top = Math.max(8, window.innerHeight - panelHeight - 8);

      const isSortDropdown = detailsEl.closest('.sorting');
      if (isSortDropdown) {
        const rightDistance = window.innerWidth - rect.right;
        panelEl.style.left = 'auto';
        panelEl.style.setProperty('right', `${rightDistance}px`, 'important');
      } else {
        let left = rect.left;
        panelEl.style.left = `${left}px`;
        panelEl.style.right = 'auto';
      }

      panelEl.style.top = `${top}px`;

      const effectiveDelay = isSortDropdown ? REVEAL_DELAY_MS_SORT : REVEAL_DELAY_MS;
      if (!isReposition && !panelEl.__revealScheduled) {
        panelEl.__revealScheduled = true;
        if (effectiveDelay === 0) {
          panelEl.style.visibility = 'visible';
          panelEl.classList.add('is-revealed');
        } else {
          panelEl.__revealTimer = setTimeout(() => {
            panelEl.style.visibility = 'visible';
            panelEl.classList.add('is-revealed');
          }, effectiveDelay);
        }
      } else if (isReposition && !panelEl.classList.contains('is-revealed')) {
        const elapsed = performance.now() - (panelEl.__openedAt || 0);
        if (elapsed >= effectiveDelay) {
          if (panelEl.__revealTimer) {
            clearTimeout(panelEl.__revealTimer);
            panelEl.__revealTimer = null;
          }
          panelEl.style.visibility = 'visible';
          panelEl.classList.add('is-revealed');
        }
      }
    };

    const onOutsideClick = (ev) => {
      if (!detailsEl.open) return cleanup();
      const isInside = detailsEl.contains(ev.target) || panelEl.contains(ev.target);
      if (!isInside) {
        detailsEl.removeAttribute('open');
        cleanup();
      }
    };
    // Ensure mobile close button (including sort dropdown) triggers full cleanup immediately
    const ensureMobileCloseBinding = () => {
      const mobileClose = panelEl.querySelector('.facets__mobile-close');
      if (mobileClose && !mobileClose.__boundClose) {
        mobileClose.__boundClose = true;
        mobileClose.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          detailsEl.removeAttribute('open');
          cleanup();
        });
      }
    };
    const onClose = () => {
      if (!detailsEl.open) {
        // Details is closing — restore panel to its original DOM position before any re-render
        cleanup();
        return;
      }
      panelEl.classList.remove('is-revealed');
      if (panelEl.__revealTimer) {
        clearTimeout(panelEl.__revealTimer);
        panelEl.__revealTimer = null;
      }
    };
    const onReposition = () => {
      if (detailsEl.open) placePanel(true);
    };
    const cleanup = () => {
      document.removeEventListener('click', onOutsideClick, true);
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition, true);
      detailsEl.removeEventListener('toggle', onClose);
      clearFloatingStyles();
      panelEl.classList.remove('is-revealed');
      panelEl.__floatingApplied = false;
      panelEl.__revealScheduled = false;
      panelEl.__openedAt = null;
      delete panelEl.__revealTimer;
    };

    const onPanelInputProxy = () => {
      if (sourceFormEl) sourceFormEl.dispatchEvent(new Event('input', { bubbles: true }));
    };
    panelEl.addEventListener('change', onPanelInputProxy, true);
    panelEl.addEventListener('input', onPanelInputProxy, true);
    ensureMobileCloseBinding();

    document.addEventListener('click', onOutsideClick, true);
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition, true);
    detailsEl.addEventListener('toggle', onClose);
    placePanel(false);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.facets__open', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    const mobileFormEl = document.getElementById('FacetFiltersFormMobile');
    const mobileDrawer = mobileFormEl && mobileFormEl.closest && mobileFormEl.closest('facet-drawer');
    if (mobileDrawer && typeof mobileDrawer.bindEvents === 'function') {
      mobileDrawer.bindEvents();
    }

    // Restore mobile form visibility when user opens filters again
    const openTrigger = document.querySelector('.mobile-facets__open');
    if (openTrigger && mobileFormEl && !openTrigger.__restoreBound) {
      openTrigger.__restoreBound = true;
      openTrigger.addEventListener('click', () => {
        mobileFormEl.classList.remove('mobile-facets--closed');
        mobileFormEl.style.display = '';
        mobileFormEl.style.visibility = 'visible';
        mobileFormEl.style.pointerEvents = 'auto';
      });
    }
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      {
        searchParams,
      },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const params = new URLSearchParams();

    // Only add non-empty values
    for (const [key, value] of formData.entries()) {
      // Skip completely empty values (but allow "0")
      if (value === '') {
        continue;
      }
      params.append(key, value);
    }

    // If this form doesn't own the availability input, carry the current URL value forward.
    // When the input IS in the form, FormData already handles it (unchecked = user removed it).
    if (!form.querySelector('[name="filter.v.availability"]')) {
      const currentAvail = new URLSearchParams(window.location.search).get('filter.v.availability');
      if (currentAvail) params.append('filter.v.availability', currentAvail);
    }

    // Auto-apply availability whenever a variant option filter (e.g. size) is active.
    // This ensures server-side filtering matches client-side availability checking.
    if ([...params.keys()].some((k) => k.startsWith('filter.v.option.')) && !params.has('filter.v.availability')) {
      params.append('filter.v.availability', '1');
    }

    return params;
  }

  mergeSearchParams(form, searchParams) {
    const params = this.createSearchParams(form);
    params.forEach((value, key) => {
      // Use set instead of append to replace existing values (prevents duplicates)
      searchParams.set(key, value);
    });
    return searchParams;
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const currentForm = event.target.closest('form');

    // Simply collect params from the form that was submitted
    const searchParams = this.createSearchParams(currentForm);
    this.onSubmitForm(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const url = new URL(event.currentTarget.href);
    const params = url.searchParams;

    FacetFiltersForm.renderPage(params.toString());
  }
}
FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

// On direct page load, auto-inject availability when variant option filters are present.
// Without this, sharing/linking a URL like ?filter.v.option.sizes=31&color=Pink would skip
// the availability filter — the facet checkbox would appear unchecked and the product count
// would include out-of-stock variants.
(function () {
  const params = new URLSearchParams(window.location.search);
  const hasVariantFilter = [...params.keys()].some((k) => k.startsWith('filter.v.option.'));
  if (hasVariantFilter && !params.has('filter.v.availability')) {
    params.set('filter.v.availability', '1');
    FacetFiltersForm.renderPage(params.toString(), null, true);
  }
})();

class FacetRemove extends HTMLElement {
  constructor() {
    super();

    this.querySelector('a').addEventListener('click', (event) => {
      event.preventDefault();
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      form.onActiveFilterClick(event);
    });
  }
}
customElements.define('facet-remove', FacetRemove);

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.min = Number(this.dataset.min);
    this.max = Number(this.dataset.max);
    this.track = this.querySelector('.price-range__track');
    this.handles = [...this.querySelectorAll('.price-range__thumbs')];
    this.startPos = 0;
    this.activeHandle;

    this.handles.forEach((handle) => {
      handle.addEventListener('mousedown', this.startMove.bind(this));
    });

    window.addEventListener('mouseup', this.stopMove.bind(this));

    this.querySelectorAll('input').forEach((element) => element.addEventListener('change', this.onRangeChange.bind(this)));
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  startMove(e) {
    this.startPos = e.offsetX;
    this.activeHandle = e.target;
    this.moveListener = this.move.bind(this);
    window.addEventListener('mousemove', this.moveListener);
  }

  move(e) {
    const isLower = this.activeHandle.classList.contains('is-lower');
    const property = isLower ? '--progress-lower' : '--progress-upper';
    const parentRect = this.track.getBoundingClientRect();
    const handleRect = this.activeHandle.getBoundingClientRect();
    let newX = e.clientX - parentRect.x - this.startPos;

    if (isLower) {
      const otherX = parseInt(this.style.getPropertyValue('--progress-upper'));
      const percentageX = (otherX * parentRect.width) / 100;
      newX = Math.min(newX, percentageX - handleRect.width);
      newX = Math.max(newX, 0 - handleRect.width / 2);
    } else {
      const otherX = parseInt(this.style.getPropertyValue('--progress-lower'));
      const percentageX = (otherX * parentRect.width) / 100;
      newX = Math.max(newX, percentageX);
      newX = Math.min(newX, parentRect.width - handleRect.width / 2);
    }

    const percentage = (newX + handleRect.width / 2) / parentRect.width;
    const valuenow = this.calcHandleValue(percentage);
    this.style.setProperty(property, percentage * 100 + '%');
    this.activeHandle.ariaValueNow = valuenow;

    const output = this.activeHandle.nextElementSibling;
    const text = output.querySelector('.price-range__output-text');
    text.innerHTML = valuenow;

    const inputs = this.querySelectorAll('input');
    const input = isLower ? inputs[0] : inputs[1];
    input.value = valuenow;

    this.adjustToValidValues(input);
    this.setMinAndMaxValues();
  }

  calcHandleValue(percentage) {
    return Math.round(percentage * (this.max - this.min) + this.min);
  }

  stopMove() {
    window.removeEventListener('mousemove', this.moveListener);
    const form = this.closest('form');

    if (this.activeHandle && form) form.dispatchEvent(new Event('input'));
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}
customElements.define('price-range', PriceRange);

class LayoutSwitcher extends HTMLElement {
  constructor() {
    super();

    this.cookieName = 'beyours:collection-layout';

    this.initLayoutMode();
    this.querySelectorAll('.list-view__item').forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)));
  }

  onButtonClick(event) {
    const target = event.target ? event.target : event;
    this.changeLayoutMode(target, target.dataset.layoutMode);
  }

  initLayoutMode() {
    if (isStorageSupported('local')) {
      const layoutMode = window.localStorage.getItem(this.cookieName);

      if (layoutMode !== null) {
        const target = this.querySelector(`.list-view__item[data-layout-mode="${layoutMode}"]`);

        if (target) {
          this.changeLayoutMode(target, layoutMode);
        }
      }
    }
  }

  changeLayoutMode(target, layoutMode) {
    const productGrid = document.getElementById('product-grid');

    if (productGrid.classList.contains('collection--empty')) {
      return;
    }

    const removedClass = ['list', 'grid', 'grid--1-col', 'grid--2-col', 'grid--3-col-tablet', 'grid--2-col-desktop', 'grid--3-col-desktop', 'grid--4-col-desktop'];
    removedClass.forEach((removed) => {
      productGrid.classList.remove(removed);
    });

    let addedClass = [];
    switch (layoutMode) {
      case 'list':
        addedClass = ['grid', 'grid--1-col', 'grid--2-col-desktop', 'list'];
        break;

      case 'grid-2':
        addedClass = ['grid', 'grid--2-col'];
        break;

      case 'grid-3':
        addedClass = ['grid', 'grid--2-col', 'grid--3-col-tablet', 'grid--3-col-desktop'];
        break;

      case 'grid-4':
        addedClass = ['grid', 'grid--2-col', 'grid--3-col-tablet', 'grid--4-col-desktop'];
        break;
    }
    addedClass.forEach((added) => {
      productGrid.classList.add(added);
    });

    this.querySelectorAll('.list-view__item').forEach((button) => button.classList.remove('list-view__item--active'));

    target.classList.add('list-view__item--active');

    if (isStorageSupported('local')) {
      window.localStorage.setItem(this.cookieName, layoutMode);
    }
  }
}
customElements.define('layout-switcher', LayoutSwitcher);

class StickyFacetFilters extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.onScrollHandler = this.onScroll.bind(this);

    window.addEventListener('scroll', this.onScrollHandler, false);
    this.onScrollHandler();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (scrollTop > this.parentNode.offsetTop) {
      window.requestAnimationFrame(this.reveal.bind(this));
    } else {
      window.requestAnimationFrame(this.reset.bind(this));
    }
  }

  reveal() {
    this.classList.add('shopify-section-filters-sticky');
  }

  reset() {
    this.classList.remove('shopify-section-filters-sticky');
  }
}
customElements.define('sticky-facet-filters', StickyFacetFilters);

class ShowMoreButton extends HTMLElement {
  constructor() {
    super();

    const attributes = {
      expanded: 'aria-expanded',
    };

    const button = this.querySelector('.button-show-more');
    if (button) {
      button.addEventListener('click', () => {
        const filter = this.closest('.js-filter');
        if (!filter) return;
        const current = filter.getAttribute(attributes.expanded);
        filter.setAttribute(attributes.expanded, (current === 'false').toString());
        this.querySelectorAll('.visually-hidden').forEach((element) => element.classList.toggle('hidden'));
      });
    }
  }
}
customElements.define('show-more-button', ShowMoreButton);

/**
 * Filters product cards based on variant availability for active filters
 * Hides products where the selected variant options (e.g., size) are not available
 */
function filterProductsByVariantAvailability() {
  // Parse active filters from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const variantOptionFilters = {};

  // Extract ONLY variant option filters (filter.v.option.X)
  // Product metafield filters (filter.p.m.custom.X) are already handled by Shopify server-side
  for (const [key, value] of urlParams.entries()) {
    if (key.startsWith('filter.v.option.')) {
      const optionName = key.replace('filter.v.option.', '');
      if (!variantOptionFilters[optionName]) {
        variantOptionFilters[optionName] = [];
      }
      variantOptionFilters[optionName].push(decodeURIComponent(value));
    }
  }

  // If no variant option filters are active, show all products
  const hasFilters = Object.keys(variantOptionFilters).length > 0;

  console.log('Active VARIANT option filters:', variantOptionFilters);
  console.log('Has variant filters:', hasFilters);

  if (!hasFilters) {
    document.querySelectorAll('.card-wrapper').forEach((card) => {
      const gridItem = card.closest('.grid__item');
      if (gridItem) {
        gridItem.style.display = '';
      }
    });
    return;
  }

  // Get all product cards
  const productCards = document.querySelectorAll('.card-wrapper[data-product-variants]');

  console.log('Found product cards:', productCards.length);

  // Also hide campaign/promo cards when size filters are active
  // These don't have variant data so we just hide them completely
  const allGridItems = document.querySelectorAll('#product-grid .grid__item');
  let campaignCardCount = 0;

  if (hasFilters) {
    allGridItems.forEach((gridItem) => {
      // Check if this grid item contains a card-wrapper (real product)
      const hasProductCard = gridItem.querySelector('.card-wrapper[data-product-variants]');

      if (!hasProductCard) {
        // This is a campaign/promo card, hide it when filters are active
        gridItem.style.display = 'none';
        campaignCardCount++;
        console.log('✗ HIDING campaign card');
      }
    });
  } else {
    allGridItems.forEach((gridItem) => {
      const hasProductCard = gridItem.querySelector('.card-wrapper[data-product-variants]');
      if (!hasProductCard) {
        gridItem.style.display = '';
      }
    });
  }

  console.log('Found campaign/promo cards:', campaignCardCount);

  productCards.forEach((card) => {
    try {
      const variantsData = JSON.parse(card.getAttribute('data-product-variants'));
      const gridItem = card.closest('.grid__item');
      const productId = card.getAttribute('data-product-id');

      if (!gridItem) return;

      // Debug: Log first few variants of first product to verify structure
      if (productCards[0] === card) {
        console.log('Sample product:', productId);
        console.log('All variants:', variantsData);
      }

      // Check if any variant matches filters AND has inventory > 0
      const matchingVariants = [];

      const hasMatchingAvailableVariant = variantsData.some((variant) => {
        // Check if variant matches ALL active variant option filters
        let matchesFilters = true;

        for (const [filterKey, filterValues] of Object.entries(variantOptionFilters)) {
          // Check if ANY of the filter values match ANY of the variant's options
          const variantMatchesThisFilter = filterValues.some((filterValue) => {
            const normalizedFilterValue = String(filterValue).trim().toLowerCase();

            return variant.options.some((optionValue) => {
              const normalizedOptionValue = String(optionValue).trim().toLowerCase();
              return normalizedOptionValue === normalizedFilterValue;
            });
          });

          // If none of the values for this filter matched, this variant doesn't match
          if (!variantMatchesThisFilter) {
            matchesFilters = false;
            break;
          }
        }

        // If variant matches filters, log it and check inventory
        if (matchesFilters) {
          const hasInventory = variant.inventory_quantity > 0;
          matchingVariants.push({
            options: variant.options,
            inventory: variant.inventory_quantity,
            hasInventory: hasInventory,
          });

          // Must have inventory > 0
          return hasInventory;
        }

        return false;
      });

      // Log matching variants for debugging
      if (matchingVariants.length > 0) {
        console.log('Product:', productId, 'Matching variants:', matchingVariants, 'Has available:', hasMatchingAvailableVariant);
      }

      // Hide product if no matching available variant found
      if (hasMatchingAvailableVariant) {
        gridItem.style.display = '';
        console.log('✓ SHOWING product:', productId);
      } else {
        gridItem.style.display = 'none';
        console.log('✗ HIDING product:', productId, '(no variants with inventory > 0)');
      }
    } catch (error) {
      console.error('Error filtering product card:', error, card);
    }
  });

  console.log('=== Filtering complete ===');
}

// Run filter on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', filterProductsByVariantAvailability);
} else {
  filterProductsByVariantAvailability();
}

// Run filter after collection is reloaded via AJAX
document.addEventListener('collection:reloaded', filterProductsByVariantAvailability);
